<template>
  <o-dialog
    ref="dialog"
    :title="$t('general.refund')"
    :info="info"
    :options="options"
    :loading="loading"
    :portal="portal"
  >
    <div>
      <div class="pt-4 text-left -mx-4 bg-gray-50 dark:bg-gray-900 border-t border-b px-4">
        <o-form v-if="item" ref="form">
          <o-field-group
            v-slot="{ status }"
            :label="$t('general.amount')"
            :name="$t('general.amount')"
            :info="$t('components.order.actions.refundPayment.amountToRefund', { amount: maxAmountLabel })"
            class="mb-4 block"
          >
            <currency-field v-model="amount" :status="status" rules="currency" />
          </o-field-group>

          <o-field-group
            v-if="allowProviderRefund"
            v-slot="{ status }"
            :label="$t('components.order.actions.refundPayment.refundWith', { name: item.refundSystemName })"
            :info="$t('components.order.actions.refundPayment.refundWithInfo')"
            :name="$t('components.order.actions.refundPayment.refundWith', { name: item.refundSystemName })"
            col
            reverse
            class="block mb-4 pt-4"
          >
            <toggle-field v-model="processRefund" :status="status" />
          </o-field-group>
        </o-form>
      </div>
    </div>
  </o-dialog>
</template>

<script setup lang="ts">
import { OrderItemRefundType } from '@/_api/models/orderItem'

import {
  CurrencyField,
  ToggleField,
} from '@/components/fields'

interface Props {
  order: Record<string, any>
  portal?: string
  loading?: boolean
}

const props = withDefaults(defineProps<Props>(), {
  portal: undefined,
  loading: false,
})

const emit = defineEmits<{
  'update:loading': [value: boolean]
  'update:order': [value: any]
}>()

const { $api, $notification, $currency, $i18n } = useNuxtApp()

const dialog = ref<any>(null)

const item = ref<any>(null)
const amount = ref(0)
const processRefund = ref(true)

const info = computed(() => {
  return item.value ? item.value.displayLabel : 'Item'
})

const options = computed(() => {
  if (item.value?.canDelete) {
    return [
      { text: $i18n.t('general.cancel'), type: 'cancel' },
      { text: $i18n.t('components.order.actions.refundPayment.deletePayment'), type: 'confirm', variant: 'warning', callback: confirmDelete },
      { text: $i18n.t('order.refund'), type: 'confirm', variant: 'danger', callback: confirmRefund },
    ]
  }

  return [
    { text: $i18n.t('general.cancel'), type: 'cancel' },
    { text: $i18n.t('order.refund'), type: 'confirm', variant: 'danger', callback: confirmRefund },
  ]
})

const currencySymbol = computed(() => {
  return props.order.data.currencySymbol
})

const maxAmountLabel = computed(() => {
  return $currency.format(item.value.price)
})

const allowProviderRefund = computed(() => {
  return item.value.refundType === OrderItemRefundType.serverHeadless
})

function open(itm: any) {
  item.value = itm
  amount.value = itm.price

  dialog.value.open()
}

async function confirmRefund() {
  emit('update:loading', true)

  try {
    const itm = item.value
    const amt = amount.value
    const process = processRefund.value
    const allow = allowProviderRefund.value
    const response = await $api.order.refundPayment(String(props.order.idOrder), {
      orderItemId: String(itm.idOrderItem),
      amount: amt,
      processorRefund: allow && process,
      customerPayment: itm.customerPayment,
    })
    const order = response.data

    emit('update:order', order)
    emit('update:loading', false)

    $notification.toast({
      title: $i18n.t('components.order.actions.refundPayment.success'),
      text: $i18n.t('components.order.actions.refundPayment.successInfo', { label: itm.displayLabel }),
      variant: 'success',
    })

    return true
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  emit('update:loading', false)
}

async function confirmDelete() {
  emit('update:loading', true)

  try {
    const itm = item.value
    const response = await $api.order.deleteItem(String(props.order.idOrder), String(itm.idOrderItem))
    const order = response.data

    emit('update:order', order)
    emit('update:loading', false)

    $notification.toast({
      title: $i18n.t('components.order.actions.refundPayment.deleteSuccess'),
      text: $i18n.t('components.order.actions.refundPayment.deleteSuccessInfo', { label: itm.displayLabel }),
      variant: 'success',
    })

    return true
  } catch (e: any) {
    console.log(e)
    $notification.error({ errors: e?.errors })
  }

  emit('update:loading', false)
}

defineExpose({ open })
</script>
